import React, { memo, useEffect, useState } from "react";
import { Button } from '@montana/shared/button';
import { ButtonStyles } from '@montana/shared/button';
import { PdpSpecificationsTab } from "@montana/pages/product-page/product-page.context";

export interface ITabProps {
    header?: string;
    content?: JSX.Element;
    fixedId?: PdpSpecificationsTab; // Allow for control of ids to control state from outside - do not mix with idPrefix.
    idPrefix?: string;
}

interface ITablistProps {
    tabs: ITabProps[];
    onTabChanged?: (tabId: string | undefined) => void;
    activeTabId?: PdpSpecificationsTab;
}

export const Tablist: React.FC<ITablistProps> = memo(({ tabs, activeTabId, onTabChanged }: ITablistProps): JSX.Element => {
    const [isDependant] = useState(!!onTabChanged && tabs.reduce((a, c) => a && !!c.fixedId, true));
    const [selectedTabId, setSelectedTabId] = useState<string>();

    useEffect(() => {
        if (isDependant) {
            setSelectedTabId(activeTabId || tabs[0].fixedId);
            onTabChanged && onTabChanged(activeTabId || tabs[0]?.fixedId);
        } else {
            setSelectedTabId(`${tabs[0].idPrefix}-0`);
        }
    }, [isDependant]);

    useEffect(() => {
        if (isDependant && activeTabId !== selectedTabId) {
            setSelectedTabId(activeTabId);
        }
    }, [activeTabId]);

    useEffect(() => {
        onTabChanged && onTabChanged(selectedTabId);
    }, [selectedTabId]);

    return (
        <div className="tab-container">
            <div role="tablist" className="tablist">
                {tabs.map((tab, i): JSX.Element => {
                    const tabId = isDependant ? tab.fixedId : `${tab.idPrefix}-${i}`;
                    const isActive = selectedTabId === tabId;

                    return (
                        <Button
                            style={ButtonStyles.Text}
                            inverseStrokeAnimation
                            strokeIsActive={isActive}
                            key={tabId}
                            role="tab"
                            id={`${tabId}-button`}
                            ariaControls={`${tabId}-content`}
                            ariaSelected={isActive ? "true" : "false"}
                            // tabIndex={isActive ? 0 : -1} // Do this when keyboard events are added to change between tabs
                            onClick={(): void => setSelectedTabId(tabId)}
                            className="tablist__tab"
                        >
                            {tab.header}
                        </Button>
                    )
                })}
            </div>
            <div>
                {tabs.map((tab, i): JSX.Element => {
                    const tabId = isDependant ? tab.fixedId : `${tab.idPrefix}-${i}`;
                    const isActive = selectedTabId === tabId;

                    return (
                        <div
                            key={tabId}
                            id={`${tabId}-content`}
                            role="tabpanel"
                            aria-labelledby={`${tabId}-button`}
                            aria-expanded={isActive ? "true" : "false"}
                            hidden={!isActive}
                        >
                            {tab.content}
                        </div>
                    )
                })}
            </div>
        </div>
    );
});
